import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const chapters = [
  {
    num: "01",
    title: "Open a case.",
    body: "Describe a situation. Pick an advisor — a Balanced Strategist, a Financial Planner — or let Boardroom match the right one after you start.",
    img: "https://images.unsplash.com/photo-1598789858997-66ad403aa8e1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxkYXJrJTIwbWluaW1hbGlzdCUyMG9mZmljZXxlbnwwfHx8fDE3ODU4MTg5Mjl8MA&ixlib=rb-4.1.0&q=85",
    alt: "Dark minimalist desk under a focused lamp",
    filter: "grayscale contrast-125 brightness-90",
  },
  {
    num: "02",
    title: "Investigate.",
    body: "Three to five sharp questions — no forms, no filler. Your live case brief takes shape as you answer.",
    img: "https://images.unsplash.com/photo-1431540015161-0bf868a2d407?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwyfHxib2FyZHJvb20lMjBtZWV0aW5nfGVufDB8fHx8MTc4NTgxODkyOXww&ixlib=rb-4.1.0&q=85",
    alt: "Empty boardroom table awaiting a meeting",
    filter: "grayscale contrast-125 brightness-75",
  },
  {
    num: "03",
    title: "Decide.",
    body: "An analysis, an advisory board, and three realistic paths — Cautious, Balanced, or Bold. The call is always yours.",
    img: "https://images.unsplash.com/photo-1483366774565-c783b9f70e2c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwY29ycG9yYXRlJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4NTgxODkyMXww&ixlib=rb-4.1.0&q=85",
    alt: "Corporate architecture in stark light",
    filter: "grayscale contrast-125 brightness-90",
  },
  {
    num: "04",
    title: "Follow up.",
    body: "Track the actions and the outcome. Every case stays in your history — active, review due, resolved — until the work is done.",
    img: "https://images.unsplash.com/photo-1764419737690-be2e35d7fa7d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwzfHxkYXJrJTIwbWluaW1hbGlzdCUyMG9mZmljZXxlbnwwfHx8fDE3ODU4MTg5Mjl8MA&ixlib=rb-4.1.0&q=85",
    alt: "Dark building facade at night",
    filter: "grayscale contrast-125 brightness-75",
  },
];

function ParallaxImage({ chapter }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <div
      ref={ref}
      className="relative aspect-[3/4] overflow-hidden rounded-none"
      data-testid={`chapter-${chapter.num}-image`}
    >
      <motion.img
        src={chapter.img}
        alt={chapter.alt}
        style={{ y }}
        className={`absolute inset-0 w-full h-[116%] object-cover ${chapter.filter}`}
      />
      <div className="absolute inset-0 bg-black/25" />
    </div>
  );
}

function ChapterRow({ chapter, index }) {
  const flip = index % 2 === 1;
  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
      <motion.div
        initial={{ opacity: 0, y: 48 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-120px" }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className={`md:col-span-5 ${flip ? "md:order-2" : ""}`}
      >
        <p
          data-testid={`chapter-${chapter.num}-number`}
          className="font-mono-label text-xs tracking-[0.3em] text-[#C9B896]/80 mb-6"
        >
          {chapter.num} —
        </p>
        <h3 className="font-serif-display font-light tracking-tighter leading-none text-4xl md:text-6xl text-white mb-6">
          {chapter.title}
        </h3>
        <p className="text-sm md:text-base leading-relaxed text-stone-400 max-w-md">
          {chapter.body}
        </p>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 64 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-120px" }}
        transition={{ duration: 1, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
        className={`md:col-span-7 ${flip ? "md:order-1" : ""} md:px-8`}
      >
        <ParallaxImage chapter={chapter} />
      </motion.div>
    </div>
  );
}

export default function Chapters() {
  return (
    <section
      data-testid="chapters-section"
      className="relative bg-[#0E0D0B] px-6 md:px-12 py-28 md:py-40"
    >
      <motion.p
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1 }}
        className="font-mono-label text-[10px] tracking-[0.3em] uppercase text-[#C9B896]/60 mb-20 md:mb-32"
      >
        The Manifesto — Four Chapters
      </motion.p>
      <div className="flex flex-col gap-28 md:gap-44">
        {chapters.map((chapter, i) => (
          <ChapterRow key={chapter.num} chapter={chapter} index={i} />
        ))}
      </div>
    </section>
  );
}
